import { computed, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { getRegionOptions } from "@utils/constants/regions";
import { getClientId } from "@utils/clientId";
import { isEmpty, deepCopy, uniq } from "@utils/toolBox";
import { getLanguageOptions } from "@/utils/constants/languages";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { useBidderTargetsStore } from "@stores/bidderTargets";
import { NotFoundError, ForbiddenError, UnauthorizedError } from "@routes/errors";
import { useSurveysStore } from "@stores/surveys";
import { useToastsStore } from "@/stores/toastsStore";
import { captureEvent } from "@/lib/analytics";

export const useMCampaignStore = defineStore("mCampaign", () => {
  const surveysStore = useSurveysStore();
  const { lightSurvey } = storeToRefs(surveysStore);

  /** @type {import("vue").Ref<import("@/types").MCampaign | null>} */
  const currentMCampaign = ref(null);
  const initialMCampaign = ref(null);
  const campaignQuotas = ref([]);
  const isNewCampaignVisible = ref(false);

  const nameMCampaign = computed(() => {
    const name = currentMCampaign.value?.name;
    const regex = /^(\d{4} \d{2} \d{2}) - (.+)$/;
    const match = name.match(regex);
    const campaignNamePart = match ? match[2].trim() : name.trim();
    if (!campaignNamePart) return "Edit Campaign";
    else return campaignNamePart;
  });
  const isSync = computed(() => currentMCampaign.value?.sync);
  const getCampaignAdAccount = computed(() => currentMCampaign.value.resources.facebook.adaccount);
  const hasMCampaignChanged = computed(() => {
    if (isEmpty(currentMCampaign.value) || isEmpty(initialMCampaign.value)) return false;
    return JSON.stringify(currentMCampaign.value) !== JSON.stringify(initialMCampaign.value);
  });

  const currentActions = computed(() => currentMCampaign.value?.alerts?.actions);
  const initialActions = computed(() => initialMCampaign.value?.alerts?.actions);
  const hasMCampaignActionsChanged = computed(() => {
    // This is not a deep comparison, it's a shallow one
    return JSON.stringify(currentActions.value) !== JSON.stringify(initialActions.value);
  });

  const hasMCampaignBidsChanged = computed(() => {
    if (isEmpty(currentMCampaign.value) || isEmpty(initialMCampaign.value)) return false;
    return JSON.stringify(currentMCampaign.value) !== JSON.stringify(initialMCampaign.value);
  });

  // actions
  const clearMCampaign = () => {
    currentMCampaign.value = null;
    initialMCampaign.value = null;
    campaignQuotas.value = [];
  };

  async function fetchMCampaign(mCampaignToken) {
    try {
      const { response, data } = await useMyFetch(`/m_campaigns/${mCampaignToken}`).json();
      if (response.value.status === 404) throw new NotFoundError("Page not found");
      if (response.value.status === 403) throw new ForbiddenError("User not allowed");
      if (response.value.status === 401) throw new UnauthorizedError("User not logged in");
      if (response.value.ok) {
        currentMCampaign.value = data.value;
        initialMCampaign.value = deepCopy(data.value);
      }
      return currentMCampaign.value;
    } catch (err) {
      handleError(err);
    }
  }

  const fetchNewCampaign = async () => {
    try {
      const { response, data } = await useMyFetch(`/m_campaigns/new`).json();

      if (response.value.ok) {
        currentMCampaign.value = data.value;
        initialMCampaign.value = deepCopy(data.value);
      }
      return currentMCampaign.value;
    } catch (err) {
      handleError(err);
    }
  };

  const setMCampaignSync = (value) => {
    currentMCampaign.value.sync = value;
    initialMCampaign.value.sync = value;
  };

  async function runBidder() {
    try {
      const { fetchBidderTargets } = useBidderTargetsStore();
      const { response, data } = await useMyFetch(`/m_campaigns/${currentMCampaign.value.token}/run_bidder`)
        .patch()
        .json();
      if (response.value.ok) {
        fetchBidderTargets(currentMCampaign.value.token);
        currentMCampaign.value = data.value;

        captureEvent("m_campaign_bidder_run", { m_campaign_token: currentMCampaign.value.token });
      }
      return currentMCampaign.value;
    } catch (err) {
      handleError(err);
    }
  }

  async function saveMCampaignActions() {
    try {
      const { response, data, error } = await useMyFetch(`/m_campaigns/${currentMCampaign.value.token}/save_actions`)
        .put({ actions: currentActions.value })
        .json();
      if (!response.value.ok) {
        const details = [error.value].flat().filter((e) => typeof e === "string");
        useToastsStore().error("The actions could not be saved", details.join("\n"));
        return false;
      }
      initialMCampaign.value.alerts.actions = deepCopy(currentMCampaign.value.alerts.actions);
      initialMCampaign.value.design_generation = data.value.design_generation;
      currentMCampaign.value.design_generation = data.value.design_generation;
      captureEvent("m_campaign_actions_saved", { m_campaign_token: currentMCampaign.value.token });
      return true;
    } catch (err) {
      handleError(err);
      return false;
    }
  }

  async function saveMCampaignBids() {
    try {
      const { response, data } = await useMyFetch(`/m_campaigns/save_bids`)
        .put({ m_campaign: currentMCampaign.value })
        .json();
      if (response.value.ok) {
        currentMCampaign.value = data.value;
        initialMCampaign.value = deepCopy(data.value);

        captureEvent("m_campaign_bids_saved", { m_campaign_token: currentMCampaign.value.token });
      }
    } catch (err) {
      handleError(err);
    }
  }

  async function saveMCampaign() {
    try {
      const { data, response } = await useMyFetch(`/m_campaigns/save`)
        .put({ m_campaign: currentMCampaign.value, client_id: getClientId() })
        .json();
      if (response.value.ok) {
        currentMCampaign.value = data.value;
        initialMCampaign.value = deepCopy(data.value);

        captureEvent("m_campaign_saved", { m_campaign_token: currentMCampaign.value.token });
      }
      return currentMCampaign.value;
    } catch (err) {
      handleError(err);
    }
  }

  async function pushMCampaign(mCampaignToken) {
    try {
      const { response, data, error } = await useMyFetch(`/m_campaigns/${mCampaignToken}/push`)
        .patch({ client_id: getClientId() })
        .json();
      if (response.value.ok) {
        currentMCampaign.value = data.value;
        initialMCampaign.value = deepCopy(data.value);

        captureEvent("m_campaign_pushed", { m_campaign_token: mCampaignToken });
      } else {
        useToastsStore().error("Push refused", error.value);
      }
    } catch (err) {
      handleError(err);
    }
  }

  const mCampaignActions = computed(() => {
    if (currentMCampaign.value.alerts.actions === undefined) currentMCampaign.value.alerts.actions = [];
    return currentMCampaign.value.alerts.actions;
  });

  const bidderStatusOptions = [
    { value: "auto", label: "Auto" },
    { value: "compute_only", label: "Compute Only" },
    { value: "off", label: "Off" },
  ];

  const autostopStatusOptions = [
    { value: "active", label: "On" },
    { value: "active_without_quotas", label: "On (without quotas)" },
    { value: "inactive", label: "Off" },
  ];

  const nightbreakStatusOptions = [
    { value: "enabled", label: "On" },
    { value: "disabled", label: "Off" },
  ];

  const quotasFullStatusOptions = computed(() => {
    const level = currentMCampaign.value.alerts.quota_full.level;
    return [
      { value: "pause", label: `Pause ${level}` },
      { value: "alert_only", label: "Alert only" },
    ];
  });

  const quotasFullLevelOptions = [
    { value: "adset", label: "Adset level" },
    { value: "campaign", label: "Campaign level" },
  ];

  const currentQuotasFullStatus = computed(() => {
    if (currentMCampaign.value.alerts?.quota_full.pause) return "pause";
    return "alert_only";
  });

  const updateQuotasFullStatus = (value) => {
    if (value === "pause") {
      currentMCampaign.value.alerts.quota_full.pause = true;
      currentMCampaign.value.alerts.quota_full.notifications = true;
    } else if (value === "alert_only") {
      currentMCampaign.value.alerts.quota_full.pause = false;
      currentMCampaign.value.alerts.quota_full.notifications = true;
    }
  };

  const localeOptions = computed(() => {
    if (isEmpty(lightSurvey.value)) return [];

    const lg = Object.values(getLanguageOptions());
    const options = Object.values(lightSurvey.value.locale_by_ids).map((locale) => ({
      label: lg.find((l) => l.value === locale.language)?.label || locale.region,
      value: `${locale.language}-${locale.token}`,
      variant: locale.variant || locale.language,
    }));

    return options;
  });

  const regionOptions = computed(() => {
    if (isEmpty(lightSurvey.value)) return [];

    const countries = uniq(lightSurvey.value.targets.flatMap((t) => t.countries));
    const regions = getRegionOptions();
    const options = countries.map((c) => {
      const option = regions.find((r) => r.value === c);
      return { label: option.label, value: option.value };
    });

    return options;
  });

  const defaultRegion = computed(() => {
    if (isEmpty(lightSurvey.value)) return "undefined";

    return lightSurvey.value.targets[0].countries[0];
  });

  const defaultLocale = computed(() => {
    if (isEmpty(lightSurvey.value)) return "undefined";

    const locale = Object.values(lightSurvey.value.locale_by_ids)[0];
    return `${locale.language}-${locale.token}`;
  });

  const currentChatToken = computed(() => {
    if (isEmpty(lightSurvey.value)) return "undefined";

    return lightSurvey.value.chat_token;
  });

  const isAlreadyPushed = computed(() => {
    if (currentMCampaign.value.last_push) return true;
    else return false;
  });

  return {
    //getters
    nameMCampaign,
    isSync,
    hasMCampaignChanged,
    currentMCampaign,
    initialMCampaign,
    hasMCampaignActionsChanged,
    hasMCampaignBidsChanged,
    campaignQuotas,
    getCampaignAdAccount,

    isNewCampaignVisible,

    //actions
    fetchMCampaign,
    setMCampaignSync,
    runBidder,
    saveMCampaignActions,
    saveMCampaignBids,
    saveMCampaign,
    pushMCampaign,

    mCampaignActions,
    bidderStatusOptions,
    autostopStatusOptions,
    nightbreakStatusOptions,
    quotasFullStatusOptions,
    quotasFullLevelOptions,
    currentQuotasFullStatus,
    updateQuotasFullStatus,

    localeOptions,
    regionOptions,
    defaultRegion,
    defaultLocale,
    currentChatToken,
    fetchNewCampaign,
    clearMCampaign,
    isAlreadyPushed,
  };
});
